import { Component, ViewEncapsulation, OnDestroy } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { InfoModalComponent } from 'app/modules/cdft/components/info-modal/info-modal.component';
import { Location } from '@angular/common';
import { StringEquipmentState } from 'app/modules/cdft/state/string-equipment.state';
import { WellStringStatusState } from 'app/modules/cdft/state/well-string-status.state';
import { clearAll } from 'app/core/state/helper';
import { CDFTService } from 'app/modules/cdft/services/cdft.service';
import { ActivatedRoute, Router } from '@angular/router';
import { CDFTState } from 'app/modules/cdft/state/cdft.state';
import { StatusModalComponent } from 'app/modules/cdft/components/status-modal/status-modal.component';
import module from 'app/modules/cdft/cdft.module';
import { CdftStatus } from 'app/modules/cdft/model';
import { UserService } from 'app/core/user/user.service';
import { OfflineDetectionService } from 'app/core/services/offline-detection.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-cdft-view',
    templateUrl: './view.page.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: module,
    styleUrls: ['./view.page.scss'],
})
export class CDFTViewPage implements OnDestroy {
    panelOpenState = false;
    isloading: boolean = false;
    isReady: boolean = true;
    status = CdftStatus;
    public cdftForm: FormGroup;
    private readonly _unsubscribeAll: Subject<any> = new Subject<any>();

    get isReadyOrApproved() {
        let status = this.state._formGroup.detail.get('status').value;
        return status == this.status.READY || status == this.status.APPROVED;
    }

    get showWfamStatus(): boolean {
        return (
            !this.isloading &&
            this.state._formGroup.detail.get('status').value !=
                this.status.DRAFT
        );
    }

    constructor(
        private dialog: MatDialog,
        public location: Location,
        private stringEquipmentState: StringEquipmentState,
        private wellStringStatusState: WellStringStatusState,
        private service: CDFTService,
        private route: ActivatedRoute,
        private router: Router,
        private state: CDFTState,
        private userService: UserService,
        private readonly offlineService: OfflineDetectionService
    ) {}

    ngOnInit() {
        this.loadCdftDetail();

        // Subscribe to online status changes and reload when coming back online
        this.offlineService.isOnline$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((isOnline) => {
                if (isOnline) {
                    // Reload data when coming back online
                    this.loadCdftDetail();
                }
            });
    }

    ngOnDestroy() {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    private loadCdftDetail() {
        this.isloading = true;
        clearAll([
            this.stringEquipmentState,
            this.wellStringStatusState,
            this.state,
        ]);
        this.service.getValveOptionType().subscribe(() => {
            const id = this.route.snapshot.paramMap.get('id');
            this.service.getCdft(id).subscribe((res) => {
                const status = res.data.status ?? '';
                this.isReady = status !== this.status.READY;

                // After getting CDFT detail, load related data using wellStringId
                const wellStringId = res.data?.cdftHeader?.wellStringId;
                if (wellStringId) {
                    // Load string equipment and well production category using the wellStringId
                    this.service.getStringEquipment(wellStringId).subscribe();
                    this.service
                        .getWellProductionCategory(wellStringId)
                        .subscribe();
                }

                this.isloading = false;
            });
        });
    }

    public openInfo() {
        this.dialog.open(InfoModalComponent, {
            width: '70%',
        });
    }

    edit() {
        const id = this.route.snapshot.paramMap.get('id');
        this.router.navigate([`/data-management/cdft/${id}/edit`]);
    }

    updateStatus() {
        const dialogRef = this.dialog.open(StatusModalComponent, {
            width: '70%',
        });

        dialogRef.afterClosed().subscribe((data) => {
            if (data?.status) {
                this.isReady = data.status !== this.status.READY;
            }
        });
    }

    // roles for approver
    approverCdftRoles(): boolean {
        let hasRole = false;

        const roles = ['Well Integrity Senior Engineer', 'Lead Well Engineer'];

        this.userService.roles$
            .subscribe((userRoles) => {
                if (userRoles.includes('Offshore Personnel')) {
                    //deny access for Offshore Personnel
                    hasRole = false;
                    return;
                }

                hasRole = userRoles.some((r) => roles.includes(r));
            })
            .unsubscribe();
        return hasRole;
    }

    // roles that can edit cdft
    hasCdftEditRoles(): boolean {
        let hasRole = false;

        const roles = new Set([
            'Well Integrity Engineer',
            'Well Integrity Desk Engineer',
        ]);

        this.userService.roles$
            .subscribe((userRoles) => {
                if (userRoles.includes('Offshore Personnel')) {
                    hasRole = false;
                    return;
                }

                hasRole = userRoles.some((r) => roles.has(r));
            })
            .unsubscribe();
        return hasRole;
    }
}
